import { useForm as useReactHookForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { FloatingLabelInput } from '@/shared/components/custom/form-field';
import { DynamicFormField } from '../FormRenderer/DynamicFormField';
import { buildSubmissionSchema } from '../../schemas';
import type { BuilderField } from './FormFieldEditorRow';

interface FormPreviewPaneProps {
  title: string;
  description?: string;
  fields: BuilderField[];
  emailRequired: boolean;
  onTitleChange?: (value: string) => void;
  onDescriptionChange?: (value: string) => void;
  readOnly?: boolean;
}

export function FormPreviewPane({
  title,
  description,
  fields,
  emailRequired,
  onTitleChange,
  onDescriptionChange,
  readOnly = false,
}: FormPreviewPaneProps) {
  const { t } = useTranslation('forms');
  const sortedFields = fields.slice().sort((a, b) => a.order - b.order);
  const schema = buildSubmissionSchema(sortedFields, emailRequired);

  const form = useReactHookForm({
    resolver: zodResolver(schema),
    defaultValues: { email: '', fields: {} },
  });

  return (
    <div data-testid="forms-builder-preview-pane">
      <Card>
        <CardHeader className="space-y-2">
          {readOnly ? (
            <h1 className="text-xl font-bold">{title || t('builder.preview.titlePlaceholder')}</h1>
          ) : (
            <Input
              value={title}
              onChange={(e) => onTitleChange?.(e.target.value)}
              placeholder={t('builder.preview.titlePlaceholder')}
              className="border-none shadow-none px-0 h-auto text-xl font-bold placeholder:font-bold placeholder:text-muted-foreground focus-visible:ring-0"
              data-testid="forms-builder-preview-title-input"
            />
          )}

          {readOnly ? (
            description && <p className="text-sm text-muted-foreground">{description}</p>
          ) : (
            <Textarea
              value={description ?? ''}
              onChange={(e) => onDescriptionChange?.(e.target.value)}
              placeholder={t('builder.preview.descriptionPlaceholder')}
              rows={1}
              className="resize-none border-none shadow-none px-0 min-h-0 text-sm text-muted-foreground focus-visible:ring-0"
              data-testid="forms-builder-preview-description-input"
            />
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {sortedFields.length === 0 ? (
            <div
              className="rounded-lg border border-dashed p-10 text-center"
              data-testid="forms-builder-preview-empty"
            >
              <p className="text-sm font-medium">{t('builder.preview.emptyTitle')}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('builder.preview.emptyDescription')}
              </p>
            </div>
          ) : (
            <>
              {emailRequired && (
                <FloatingLabelInput
                  id="forms-builder-preview-email-input"
                  label={t('renderer.emailLabel')}
                  type="email"
                  disabled
                  value=""
                  readOnly
                  data-testid="forms-builder-preview-email-input"
                />
              )}
              {sortedFields.map((field) => (
                <DynamicFormField key={field.localId} field={field} control={form.control} />
              ))}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
